import { useRouter } from "expo-router";
import { useState } from "react";
import {
  KeyboardAvoidingView,
  Platform,
  Pressable,
  StyleSheet,
  View,
} from "react-native";
import { Button, Card, Input, Screen, Text } from "~/components/ui";
import { TurnstileField } from "~/components/TurnstileField";
import { useAuth } from "~/lib/auth";
import { useTurnstile } from "~/lib/useTurnstile";
import { radius, spacing, type ThemeColors } from "~/styles/tokens";
import { useTheme, useThemedStyles } from "~/styles/theme";

type Tab = "login" | "register" | "forgotPassword";

export default function AuthScreen() {
  const router = useRouter();
  const { colors } = useTheme();
  const styles = useStyles();
  const {
    user,
    convertToEmailPassword,
    signInWithEmailPassword,
    resetPassword,
  } = useAuth();
  const isAnonymous = !user;

  const [tab, setTab] = useState<Tab>(isAnonymous ? "register" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState(
    user?.name === "Guest Baker" ? "" : (user?.name ?? ""),
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const turnstile = useTurnstile();

  const switchTab = (next: Tab) => {
    setTab(next);
    setError(null);
    setInfo(null);
  };

  const handleSubmit = async () => {
    setError(null);
    setInfo(null);
    if (!email.trim() || !password.trim()) {
      setError("Email and password are required.");
      return;
    }
    setLoading(true);
    try {
      if (tab === "login") {
        const result = await signInWithEmailPassword(
          email,
          password,
          turnstile.captchaToken,
        );
        if (result.error) {
          setError(result.error);
          return;
        }
        router.back();
        return;
      }

      if (!name.trim()) {
        setError("Please enter your name.");
        return;
      }
      const result = await convertToEmailPassword(
        email,
        password,
        name.trim(),
        turnstile.captchaToken,
      );
      if (result.error) {
        setError(result.error);
        return;
      }
      setInfo("Account saved. Your starter and timers stay on this baker.");
      router.back();
    } finally {
      setLoading(false);
      turnstile.reset();
    }
  };

  const handleForgotPassword = async () => {
    if (!email.trim()) {
      setError("Enter your email address first.");
      return;
    }
    setLoading(true);
    setError(null);
    const result = await resetPassword(email, turnstile.captchaToken);
    setLoading(false);
    turnstile.reset();
    if (result.error) {
      setError(result.error);
      return;
    }
    setInfo(result.message ?? "Check your email for a reset link.");
  };

  const messages = (
    <>
      {info ? (
        <Text variant="caption" color={colors.success} style={styles.message}>
          {info}
        </Text>
      ) : null}
      {error ? (
        <Text variant="caption" color={colors.danger} style={styles.message}>
          {error}
        </Text>
      ) : null}
    </>
  );

  if (tab === "forgotPassword") {
    return (
      <KeyboardAvoidingView
        style={styles.flex}
        behavior={Platform.OS === "ios" ? "padding" : "height"}
      >
        <Screen scroll width="narrow" centered>
          <View style={styles.header}>
            <Text style={styles.logo}>🔑</Text>
            <Text variant="heading" align="center">
              Reset password
            </Text>
            <Text variant="body" align="center" color={colors.muted}>
              We&apos;ll send a reset link to your email.
            </Text>
          </View>
          <Card elevation="raised" style={styles.formCard}>
            <Input
              label="Email"
              placeholder="you@example.com"
              value={email}
              onChangeText={setEmail}
              keyboardType="email-address"
              autoCapitalize="none"
              autoComplete="email"
            />
            {messages}
            <TurnstileField
              onToken={turnstile.setToken}
              resetKey={turnstile.resetKey}
            />
            <Button
              label="Send reset link"
              onPress={() => void handleForgotPassword()}
              disabled={!turnstile.passed}
              loading={loading}
              variant="primary"
              fullWidth
              style={styles.submit}
            />
          </Card>
          <Button
            label="Back to sign in"
            onPress={() => switchTab("login")}
            variant="ghost"
            style={styles.footerBtn}
          />
        </Screen>
      </KeyboardAvoidingView>
    );
  }

  return (
    <KeyboardAvoidingView
      style={styles.flex}
      behavior={Platform.OS === "ios" ? "padding" : "height"}
    >
      <Screen scroll width="narrow" centered>
        <View style={styles.header}>
          <Text style={styles.logo}>🍞</Text>
          <Text variant="heading" align="center">
            {tab === "login"
              ? "Welcome back"
              : Platform.OS === "web"
                ? "Create your account"
                : "Keep this baker"}
          </Text>
          <Text variant="body" align="center" color={colors.muted}>
            {tab === "login"
              ? "Sign in to open your starter on this device"
              : Platform.OS === "web"
                ? "Sign up to start your starter and timers in sync"
                : "Convert this guest session so starters and timers sync"}
          </Text>
        </View>

        <View style={styles.segment}>
          {(["login", "register"] as const).map((value) => {
            const active = tab === value;
            const label =
              value === "login"
                ? "Sign in"
                : isAnonymous
                  ? "Save account"
                  : "Register";
            return (
              <Pressable
                key={value}
                accessibilityRole="tab"
                accessibilityLabel={label}
                accessibilityState={{ selected: active }}
                onPress={() => switchTab(value)}
                style={[styles.segmentItem, active && styles.segmentItemActive]}
              >
                <Text
                  variant="label"
                  style={[
                    styles.segmentText,
                    active && styles.segmentTextActive,
                  ]}
                >
                  {label}
                </Text>
              </Pressable>
            );
          })}
        </View>

        <Card elevation="raised" style={styles.formCard}>
          {tab === "register" ? (
            <Input
              label="Your name"
              placeholder="Baker's name"
              value={name}
              onChangeText={setName}
              autoCapitalize="words"
              autoComplete="username"
            />
          ) : null}
          <Input
            label="Email"
            placeholder="you@example.com"
            value={email}
            onChangeText={setEmail}
            keyboardType="email-address"
            autoCapitalize="none"
            autoComplete="email"
          />
          <Input
            label="Password"
            placeholder="••••••••"
            value={password}
            onChangeText={setPassword}
            secureTextEntry
            autoComplete={tab === "register" ? "new-password" : "password"}
          />
          {messages}
          <TurnstileField
            onToken={turnstile.setToken}
            resetKey={turnstile.resetKey}
          />
          <Button
            label={tab === "login" ? "Sign in" : "Save account"}
            onPress={() => void handleSubmit()}
            disabled={!turnstile.passed}
            loading={loading}
            variant="primary"
            fullWidth
            style={styles.submit}
          />
          {tab === "login" ? (
            <Button
              label="Forgot password?"
              onPress={() => switchTab("forgotPassword")}
              variant="ghost"
              fullWidth
              style={styles.forgotBtn}
            />
          ) : null}
        </Card>

        {/* Guests exist only in the mobile apps; the web app needs an account. */}
        {Platform.OS !== "web" ? (
          <Button
            label="Continue as guest"
            onPress={() => router.back()}
            variant="ghost"
            style={styles.footerBtn}
          />
        ) : null}
      </Screen>
    </KeyboardAvoidingView>
  );
}

const useStyles = () => useThemedStyles(makeStyles);

const makeStyles = (c: ThemeColors) =>
  StyleSheet.create({
    flex: {
      flex: 1,
    },
    header: {
      alignItems: "center",
      paddingBottom: spacing.lg,
      gap: spacing.xs,
    },
    logo: {
      fontSize: 48,
      lineHeight: 56,
    },
    segment: {
      flexDirection: "row",
      backgroundColor: c.surfaceMuted,
      borderRadius: radius.full,
      padding: 4,
      marginBottom: spacing.md,
    },
    segmentItem: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      paddingVertical: spacing.sm + 2,
      borderRadius: radius.full,
    },
    segmentItemActive: {
      backgroundColor: c.surface,
    },
    segmentText: {
      color: c.heading,
    },
    segmentTextActive: {
      color: c.primary,
    },
    formCard: {
      gap: spacing.xs,
      marginBottom: spacing.md,
    },
    message: {
      marginTop: spacing.xs,
    },
    submit: {
      marginTop: spacing.sm,
    },
    forgotBtn: {
      marginTop: spacing.xs,
    },
    footerBtn: {
      alignSelf: "center",
    },
  });
